import { ComponentProps, forwardRef } from 'react'
import { cn } from '@/ui/utils/style'
import { Typography, TypographyProps } from '@/ui/atoms/typography/Typography'

type DivProps = ComponentProps<'div'>

interface CardType extends React.ForwardRefExoticComponent<DivProps & React.RefAttributes<HTMLDivElement>> {
  Label: React.FC<TypographyProps>
  Value: React.FC<DivProps>
  Content: React.FC<DivProps>
  ContentContainer: React.FC<DivProps>
  ButtonContainer: React.FC<DivProps>
  Item: React.FC<DivProps>
}

const CardRoot = forwardRef<HTMLDivElement, DivProps>(({ className, ...rest }, ref) => {
  return <div ref={ref} className={cn(className)} {...rest} />
})
CardRoot.displayName = 'Card'

function Label({ className, ...rest }: TypographyProps) {
  return (
    <Typography
      variant="prompt"
      className={cn(
        'font-bold text-foreground text-sm sm:h-4 md:font-normal md:text-muted-foreground md:text-xs',
        className,
      )}
      {...rest}
    />
  )
}
Label.displayName = 'Card.Label'

function Value({ className, ...rest }: DivProps) {
  return <div className={cn('text-foreground text-sm leading-none sm:text-base', className)} {...rest} />
}
Value.displayName = 'Card.Value'

function Content({ className, ...rest }: DivProps) {
  return (
    <div
      className={cn('flex w-full flex-col justify-between gap-4 md:flex-row md:items-center', className)}
      {...rest}
    />
  )
}
Content.displayName = 'Card.Content'

function Item({ className, ...rest }: DivProps) {
  return (
    <div
      className={cn('flex flex-1 flex-row items-start justify-between gap-1 md:flex-col md:justify-start', className)}
      {...rest}
    />
  )
}
Item.displayName = 'Card.Item'

function ContentContainer({ className, ...rest }: DivProps) {
  return <div className={cn('flex flex-1 flex-col gap-2 md:flex-row', className)} {...rest} />
}
ContentContainer.displayName = 'Card.ContentContainer'

function ButtonContainer({ className, ...rest }: DivProps) {
  return <div className={cn('ml-2` flex w-[72px] flex-col pt-5', className)} {...rest} />
}
ButtonContainer.displayName = 'Card.ButtonContainer'

export const Card = Object.assign(CardRoot, {
  Label,
  Value,
  Content,
  ContentContainer,
  ButtonContainer,
  Item,
}) as CardType
